/**
 * Vocabolario `phase` delle commesse (BE v1.21: era `status` con valori liberi).
 * La state-machine è enforced server-side (422 INVALID_STATUS_TRANSITION):
 * draft→planned|cancelled, planned→in_progress|on_hold|cancelled,
 * in_progress→on_hold|completed|cancelled, on_hold→in_progress|cancelled,
 * completed→archived, cancelled→archived; draft irreversibile, archived terminale.
 */
export const PHASE_OPTIONS = [
  { value: 'draft', label: 'Bozza' },
  { value: 'planned', label: 'Pianificata' },
  { value: 'in_progress', label: 'In corso' },
  { value: 'on_hold', label: 'In pausa' },
  { value: 'completed', label: 'Completata' },
  { value: 'cancelled', label: 'Annullata' },
  { value: 'archived', label: 'Archiviata' },
] as const;

/** Fasi fuori vocabolario ancora presenti su righe vecchie: si leggono, non si scelgono. */
const LEGACY_PHASE_LABELS: Record<string, string> = { closed: 'Chiusa' };

export function phaseLabel(phase?: string | null): string {
  if (!phase) return '—';
  return PHASE_OPTIONS.find((o) => o.value === phase)?.label ?? LEGACY_PHASE_LABELS[phase] ?? phase;
}

/** Fasi "aperte" (commessa lavorabile) per filtri tipo picker/planning. */
export const OPEN_PHASES = ['draft', 'planned', 'in_progress', 'on_hold'] as const;

/**
 * Le fasi in cui un progetto si può **scegliere su un task** (nuovo, o cambiando quello di un task).
 *
 * Più strette di `OPEN_PHASES`: un progetto sospeso (`on_hold`) resta aperto per la rendicontazione e
 * la pianificazione, ma non riceve lavoro nuovo — è la regola del MaeWay v8, da cui MAESTRALE GROUP
 * porta i progetti, e vale per tutte le aziende (BE-TO-FE-CONNETTORE-MAEWAY-V8-v1 §2). Chiusi,
 * annullati e archiviati sono fuori anche lì.
 */
export const TASK_SELECTABLE_PHASES = ['draft', 'planned', 'in_progress'] as const;

/** La fase non è più sceglibile su un task: l'etichetta lo dice, invece di svuotare il campo. */
export function isTaskSelectablePhase(phase?: string | null): boolean {
  return !phase || (TASK_SELECTABLE_PHASES as readonly string[]).includes(phase);
}

/**
 * Matrice transizioni ammesse dalla state-machine BE (v1.22, enforced sul PATCH).
 * `draft` è irreversibile, `archived` terminale. Fuori matrice → 422 INVALID_STATUS_TRANSITION.
 */
export const PHASE_TRANSITIONS: Record<string, string[]> = {
  draft: ['planned', 'cancelled'],
  planned: ['in_progress', 'on_hold', 'cancelled'],
  in_progress: ['on_hold', 'completed', 'cancelled'],
  on_hold: ['in_progress', 'cancelled'],
  completed: ['archived'],
  cancelled: ['archived'],
  archived: [],
};

/** Options per il dropdown "cambia fase": stato corrente + solo le transizioni valide. */
export function phaseTransitionOptions(current?: string | null) {
  const cur = current ?? 'draft';
  const next = PHASE_TRANSITIONS[cur] ?? [];
  return PHASE_OPTIONS.filter((o) => o.value === cur || next.includes(o.value));
}

/** true se dallo stato corrente non è possibile alcuna transizione (es. archived). */
export function isTerminalPhase(current?: string | null): boolean {
  return (PHASE_TRANSITIONS[current ?? 'draft'] ?? []).length === 0;
}
